// S2-0f: Agent Control Center — sidebar + overview + run evaluation
import { useState, useEffect } from 'react';
import { AgentSidebar } from '../components/AgentSidebar';
import { AgentOverview } from '../components/AgentOverview';
import { RunEvaluation } from '../components/RunEvaluation';
import { useAgents } from '../hooks/useAgents';
import { useAgentDetail } from '../hooks/useAgentDetail';

type RightPanelView = 'overview' | 'run-eval';

export default function EvalDashboard() {
  const { agents, loading, error, refetch } = useAgents();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { detail, loading: detailLoading, error: detailError } = useAgentDetail(selectedId);
  const [rightPanel, setRightPanel] = useState<RightPanelView>('overview');

  // Auto-select first agent once loaded
  useEffect(() => {
    if (!selectedId && agents.length > 0 && !loading) {
      setSelectedId(agents[0].id);
    }
  }, [selectedId, agents, loading]);

  // Reset to overview when agent changes
  useEffect(() => {
    setRightPanel('overview');
  }, [selectedId]);

  return (
    <div style={{ display: 'flex', height: '100vh', background: 'var(--page-bg)' }}>
      {/* Left rail */}
      <AgentSidebar
        agents={agents}
        loading={loading}
        error={error}
        selectedId={selectedId}
        onSelect={setSelectedId}
      />

      {/* Right panel */}
      <main style={{ flex: 1, overflow: 'auto' }}>
        {/* Header bar */}
        <header style={{
          height: 'var(--header-height)',
          borderBottom: '1px solid var(--border-default)',
          display: 'flex',
          alignItems: 'center',
          padding: '0 var(--space-6)',
          gap: 'var(--space-3)',
          flexShrink: 0,
        }}>
          <svg width="24" height="24" viewBox="0 0 64 64" fill="none">
            <rect x="22" y="14" width="20" height="8" rx="3" fill="var(--signal-400)" />
            <rect x="16" y="28" width="32" height="8" rx="3" fill="var(--signal-400)" opacity="0.7" />
            <rect x="10" y="42" width="44" height="8" rx="3" fill="var(--signal-400)" opacity="0.4" />
          </svg>
          <span style={{ fontWeight: 700, fontSize: 'var(--text-lg)', letterSpacing: '0.06em' }}>
            <span style={{ color: 'var(--text-primary)' }}>Air</span>
            <span style={{ color: 'var(--signal-400)' }}>gent</span>
          </span>
          <span style={{ width: '1px', height: '16px', background: 'var(--border-default)' }} />
          <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text-primary)' }}>
            Agent Control Center
          </span>
          <button
            onClick={refetch}
            disabled={loading}
            title="Refresh agent data"
            style={{
              marginLeft: 'auto',
              background: 'transparent',
              border: 'none',
              color: 'var(--text-muted)',
              cursor: loading ? 'wait' : 'pointer',
              padding: 'var(--space-2)',
              borderRadius: 'var(--radius-md)',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M2 8a6 6 0 0110.47-4M14 8a6 6 0 01-10.47 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M12.47 1v3h-3M3.53 15v-3h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </header>

        {/* Content area */}
        {detail && rightPanel === 'run-eval' ? (
          <RunEvaluation
            agent={detail}
            onBack={() => setRightPanel('overview')}
          />
        ) : detail && rightPanel === 'overview' ? (
          <AgentOverview
            detail={detail}
            onRunEval={() => setRightPanel('run-eval')}
          />
        ) : detailLoading ? (
          <div style={{ padding: 'var(--space-12)', textAlign: 'center', color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
            Loading agent detail...
          </div>
        ) : detailError ? (
          <div style={{ padding: 'var(--space-12)', textAlign: 'center', color: 'var(--error-dark-text)', fontSize: 'var(--text-sm)' }}>
            {detailError}
          </div>
        ) : (
          <div style={{ padding: 'var(--space-8) var(--space-6)', maxWidth: 'var(--max-width-content)' }}>
            <div style={{
              background: 'var(--surface-1)',
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius-xl)',
              padding: 'var(--space-16)',
              textAlign: 'center',
            }}>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
                Select an agent from the sidebar to view details
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
